import React, { useEffect, useState, useCallback, memo } from 'react';
import { FiAlertTriangle, FiCheck, FiCopy, FiTrash2 } from 'react-icons/fi';
import AdminLayout from './AdminLayout';
import { useNavRefresh } from '../../context/NavContext';
import API from '../../utils/api';
import styles from './AdminCandidates.module.css';
import AdminPagination from './AdminPagination';

const ROLES = [
  { value: 'RSM', label: 'Regional Sales Manager (RSM)' },
  { value: 'FSM', label: 'Field Sales Manager (FSM)' },
  { value: 'TSE', label: 'Territory Sales Manager (TSE)' },
];

const CandidateRowMemo = memo(function CandidateRow({ c, serialNumber, copiedId, onCopyClick, onDeleteClick, onToggleActivation, onToggleAssessmentPause, getStatusClass, getStatusLabel, toggleLoading, assessmentToggleLoading, styles }) {
  const createdDate = new Date(c.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  
  return (
    <tr key={c._id}>
      <td>{serialNumber}</td>
      <td>
        <div className={styles.candidateCell}>
          <div>
            <div className={styles.candidateName}>{c.fullName}</div>
            <div className={styles.candidateEmail}>{c.email}</div>
          </div>
        </div>
      </td>
      <td><span className={styles.roleTag}>{c.role}</span></td>
      <td>
        <div className={styles.codeCell}>
          <span className={styles.codeValue}>{c.rawPassword}</span>
          <button 
            className={styles.copyBtn} 
            onClick={() => onCopyClick(c.rawPassword, c._id)}
            title={copiedId === c._id ? 'Copied!' : 'Copy code'}
          >
            {copiedId === c._id ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
          </button>
        </div>
      </td>
      <td><span className={`${styles.statusTag} ${getStatusClass(c.status)}`}>{c.assessmentPaused ? 'Paused' : getStatusLabel(c.status)}</span></td>
      <td className={styles.muted}>{createdDate}</td>
      <td>
        <button 
          className={`${styles.toggleBtn} ${c.deactivated ? styles.toggleBtnInactive : ''}`}
          onClick={() => onToggleActivation(c._id)}
          disabled={toggleLoading === c._id}
          title={c.deactivated ? 'Activate candidate' : 'Deactivate candidate'}
        >
          {toggleLoading === c._id ? '...' : (c.deactivated ? 'Activate' : 'Deactivate')}
        </button>
      </td>
      <td>
        {c.status === 'in_progress' && !c.deactivated && (
          <button
            className={`${styles.assessmentToggleBtn} ${c.assessmentPaused ? styles.assessmentResumeBtn : ''}`}
            onClick={() => onToggleAssessmentPause(c._id, c.assessmentPaused)}
            disabled={assessmentToggleLoading === c._id}
            title={c.assessmentPaused ? 'Resume assessment' : 'Pause assessment'}
          >
            {assessmentToggleLoading === c._id ? '...' : (c.assessmentPaused ? 'Resume assessment' : 'Pause assessment')}
          </button>
        )}
      </td>
      <td>
        <button className={styles.deleteIconBtn} onClick={() => onDeleteClick(c._id)} title="Delete candidate" aria-label={`Delete ${c.fullName}`}>
          <FiTrash2 aria-hidden="true" />
        </button>
      </td>
    </tr>
  );
});

export default function AdminCandidates() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', role: 'RSM' });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [newCredential, setNewCredential] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [assessmentAction, setAssessmentAction] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(null);
  const [assessmentToggleLoading, setAssessmentToggleLoading] = useState(null);
  const [toggleSuccess, setToggleSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusCounts, setStatusCounts] = useState({ all: 0, pending: 0, in_progress: 0, completed: 0 });
  const { refreshKey } = useNavRefresh();

  const filteredCandidates = candidates.filter(c => 
    c.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredCandidates.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedCandidates = filteredCandidates.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = statusFilter === 'all' ? '' : `&status=${statusFilter}`;
      const res = await API.get(`/admin/candidates?${statusParam}`);
      // Handle both array response (backward compatible) and object response with counts
      if (Array.isArray(res.data)) {
        setCandidates(res.data);
      } else {
        setCandidates(res.data.data || []);
        if (res.data.counts) {
          setStatusCounts(res.data.counts);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter]);

  const copyToClipboard = useCallback((code, candidateId) => {
    navigator.clipboard.writeText(code);
    setCopiedId(candidateId);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  const getStatusClass = useCallback(s => ({ pending: styles.statusPending, in_progress: styles.statusActive, completed: styles.statusDone }[s] || ''), [styles]);
  const getStatusLabel = useCallback(s => ({ pending: 'Pending', in_progress: 'In Progress', completed: 'Completed' }[s] || s), []);

  useEffect(() => { load(); }, [refreshKey, load]);

  // Auto-dismiss credential card after 10 seconds
  useEffect(() => {
    if (newCredential) {
      const timer = setTimeout(() => {
        setNewCredential(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [newCredential]);

  const handleCreate = useCallback(async e => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const res = await API.post('/admin/candidates', form);
      setNewCredential(res.data.candidate);
      setForm({ fullName: '', email: '', role: 'RSM' });
      setShowForm(false);
      await load();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create candidate.');
    } finally {
      setFormLoading(false);
    }
  }, [form, load]);

  const handleDelete = useCallback(async id => {
    setDeleteLoading(true);
    try {
      await API.delete(`/admin/candidates/${id}`);
      setCandidates(p => p.filter(c => c._id !== id));
      setDeleteSuccess(true);
      setDeleteId(null);
      // Auto-dismiss success message after 3 seconds
      setTimeout(() => setDeleteSuccess(false), 3000);
    } catch (e) { 
      console.error(e);
      alert('Failed to delete candidate. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  }, []);

  const handleToggleActivation = useCallback(async id => {
    setToggleLoading(id);
    try {
      const candidate = candidates.find(c => c._id === id);
      const isDeactivated = candidate?.deactivated;
      
      if (isDeactivated) {
        // Reactivate
        await API.patch(`/admin/candidates/${id}/reactivate`);
        setCandidates(p => p.map(c => c._id === id ? { ...c, deactivated: false, deactivatedAt: null } : c));
      } else {
        // Deactivate
        await API.patch(`/admin/candidates/${id}/deactivate`);
        setCandidates(p => p.map(c => c._id === id ? { ...c, deactivated: true, deactivatedAt: new Date() } : c));
      }
      setToggleSuccess(true);
      // Auto-dismiss success message after 3 seconds
      setTimeout(() => setToggleSuccess(false), 3000);
    } catch (e) { 
      console.error(e);
      alert('Failed to toggle candidate status. Please try again.');
    } finally {
      setToggleLoading(null);
    }
  }, [candidates]);

  const handleToggleAssessmentPause = useCallback(async (id, isPaused) => {
    setAssessmentToggleLoading(id);
    try {
      const action = isPaused ? 'resume-assessment' : 'pause-assessment';
      await API.patch(`/admin/candidates/${id}/${action}`);
      setCandidates(p => p.map(c => c._id === id
        ? { ...c, assessmentPaused: !isPaused, assessmentPausedAt: isPaused ? null : new Date() }
        : c
      ));
      setToggleSuccess(true);
      setTimeout(() => setToggleSuccess(false), 3000);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to update assessment access. Please try again.');
    } finally {
      setAssessmentToggleLoading(null);
    }
  }, []);

  const confirmAssessmentAction = useCallback(() => {
    if (!assessmentAction) return;
    handleToggleAssessmentPause(assessmentAction.id, assessmentAction.isPaused);
    setAssessmentAction(null);
  }, [assessmentAction, handleToggleAssessmentPause]);

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h2 className={styles.pageTitle}>Candidates</h2>
            <p className={styles.pageSub}>Create and manage candidate access codes</p>
          </div>
          <button className={styles.actionBtn} onClick={() => { setShowForm(true); setFormError(''); }}>
            + Add Candidate
          </button>
        </div>

        {/* Search Bar */}
        <div className={styles.searchBar}>
          <div className={styles.filtersInline}>
            {[
              { key: 'all', label: 'All', count: statusCounts.all },
              { key: 'pending', label: 'Pending', count: statusCounts.pending },
              { key: 'in_progress', label: 'In Progress', count: statusCounts.in_progress },
              { key: 'completed', label: 'Completed', count: statusCounts.completed }
            ].map(f => (
              <button 
                key={f.key} 
                className={`${styles.filterBtn} ${statusFilter === f.key ? styles.active : ''}`} 
                onClick={() => { setStatusFilter(f.key); }}
              >
                {f.label} <span className={styles.filterCount}>({f.count})</span>
              </button>
            ))}
          </div>
          <input 
            type="text" 
            className={styles.searchInput}
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          />
        </div>

        {/* Credential Popup Card */}
        {newCredential && (
          <div className={styles.credPopupOverlay} onClick={() => setNewCredential(null)}>
            <div className={styles.credPopupCard} onClick={e => e.stopPropagation()}>
              <button className={styles.credPopupClose} onClick={() => setNewCredential(null)}>×</button>
              <div className={styles.credPopupContent}>
                <div className={styles.credPopupIcon}><FiCheck aria-hidden="true" /></div>
                <h3 className={styles.credPopupTitle}>Candidate Created</h3>
                <p className={styles.credPopupSubtitle}>{newCredential.fullName}</p>
                
                <div className={styles.credPopupItem}>
                  <span className={styles.credPopupLabel}>Access Code</span>
                  <div className={styles.credPopupCopyRow}>
                    <span className={styles.credPopupCode}>{newCredential.password}</span>
                    <button 
                      className={styles.credPopupCopyBtn}
                      onClick={() => {
                        navigator.clipboard.writeText(newCredential.password);
                        // Optional: Show toast notification
                      }}
                      title="Copy to clipboard"
                    >
                      <FiCopy aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className={styles.credPopupItem}>
                  <span className={styles.credPopupLabel}>Role</span>
                  <span className={styles.credPopupValue}>{ROLES.find(r => r.value === newCredential.role)?.label}</span>
                </div>

                <div className={styles.credPopupItem}>
                  <span className={styles.credPopupLabel}>Login URL</span>
                  <span className={styles.credPopupValue}>{window.location.origin}/login</span>
                </div>

                <p className={styles.credPopupWarning}><FiAlertTriangle aria-hidden="true" /> Save the access code now. It will not be shown again.</p>
              </div>
            </div>
          </div>
        )}

        {/* Delete Success Message */}
        {deleteSuccess && (
          <div className={styles.successBox}>
            <div className={styles.successContent}>
              <span className={styles.successIcon}><FiCheck aria-hidden="true" /></span>
              <span className={styles.successText}>Candidate deleted successfully</span>
            </div>
          </div>
        )}

        {/* Toggle Success Message */}
        {toggleSuccess && (
          <div className={styles.successBox}>
            <div className={styles.successContent}>
              <span className={styles.successIcon}><FiCheck aria-hidden="true" /></span>
              <span className={styles.successText}>Candidate status updated successfully</span>
            </div>
          </div>
        )}

        {/* Create Form Modal */}
        {showForm && (
          <div className={styles.modalOverlay} onClick={() => setShowForm(false)}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Add New Candidate</h3>
                <button className={styles.modalClose} onClick={() => setShowForm(false)}>×</button>
              </div>
              {formError && <div className={styles.formError}>{formError}</div>}
              <form onSubmit={handleCreate} className={styles.form}>
                <div className={styles.field}>
                  <label className={styles.label}>Full Name</label>
                  <input className={styles.input} value={form.fullName} onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))} placeholder="Candidate's full name" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Email Address</label>
                  <input className={styles.input} type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="candidate@email.com" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Role Applied For</label>
                  <select className={styles.select} value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className={styles.modalActions}>
                  <button type="button" className={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="submit" className={styles.submitBtn} disabled={formLoading}>
                    {formLoading ? 'Creating...' : 'Create & Generate Code'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirm */}
        {deleteId && (
          <div className={styles.modalOverlay} onClick={() => setDeleteId(null)}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <div className={styles.deleteModalContent}>
                <div className={styles.deleteIcon}><FiAlertTriangle aria-hidden="true" /></div>
                <h3 className={styles.deleteTitle}>Delete Candidate</h3>
                <p className={styles.deleteMessage}>
                  Are you sure you want to delete this candidate? This action will permanently remove the candidate and all their assessment data.
                </p>
                <div className={styles.deleteWarning}>
                  <FiAlertTriangle aria-hidden="true" />
                  <span>This cannot be undone</span>
                </div>
                <div className={styles.modalActions}>
                  <button className={styles.cancelBtn} onClick={() => setDeleteId(null)} disabled={deleteLoading}>Keep Candidate</button>
                  <button className={styles.deleteBtn} onClick={() => handleDelete(deleteId)} disabled={deleteLoading}>
                    {deleteLoading ? 'Deleting...' : 'Delete Permanently'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {assessmentAction && (
          <div className={styles.modalOverlay} onClick={() => setAssessmentAction(null)}>
            <div
              className={styles.modal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="assessment-action-title"
              aria-describedby="assessment-action-message"
              onClick={e => e.stopPropagation()}
            >
              <div className={styles.deleteModalContent}>
                <div className={styles.deleteIcon}><FiAlertTriangle aria-hidden="true" /></div>
                <h3 id="assessment-action-title" className={styles.deleteTitle}>
                  {assessmentAction.isPaused ? 'Resume Assessment' : 'Pause Assessment'}
                </h3>
                <p id="assessment-action-message" className={styles.deleteMessage}>
                  {assessmentAction.isPaused
                    ? 'Allow this candidate to continue their assessment from where they stopped? Their saved answers and remaining time will be preserved.'
                    : 'Stop this candidate from continuing their assessment? They will see a notice to contact the administrator. Their saved answers and remaining time will be preserved.'}
                </p>
                <div className={styles.modalActions}>
                  <button className={styles.cancelBtn} onClick={() => setAssessmentAction(null)}>
                    Cancel
                  </button>
                  <button
                    className={assessmentAction.isPaused ? styles.submitBtn : styles.deactivateBtn}
                    onClick={confirmAssessmentAction}
                    disabled={assessmentToggleLoading === assessmentAction.id}
                  >
                    {assessmentAction.isPaused ? 'Resume Assessment' : 'Pause Assessment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className={styles.tableCard}>
          {loading ? (
            <div className={styles.empty}>Loading candidates...</div>
          ) : filteredCandidates.length === 0 ? (
            <div className={styles.empty}>{searchTerm ? 'No candidates match your search.' : 'No candidates yet. Click "Add Candidate" to get started.'}</div>
          ) : (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Candidate</th>
                      <th>Role</th>
                      <th>Access Code</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Activation</th>
                      <th>Assessment access</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCandidates.map((c, index) => (
                      <CandidateRowMemo 
                        key={c._id} 
                        c={c} 
                        serialNumber={(currentPage - 1) * pageSize + index + 1}
                        copiedId={copiedId}
                        onCopyClick={copyToClipboard}
                        onDeleteClick={setDeleteId}
                        onToggleActivation={handleToggleActivation}
                        onToggleAssessmentPause={(id, isPaused) => setAssessmentAction({ id, isPaused })}
                        toggleLoading={toggleLoading}
                        assessmentToggleLoading={assessmentToggleLoading}
                        getStatusClass={getStatusClass}
                        getStatusLabel={getStatusLabel}
                        styles={styles}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <AdminPagination
                page={currentPage}
                pageSize={pageSize}
                total={filteredCandidates.length}
                onPageChange={setPage}
                onPageSizeChange={size => { setPageSize(size); setPage(1); }}
              />
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
